import React, { useEffect, useState } from 'react';
import { Origin, EditorType } from '../../manage-installed-apps-page';
import { appHasEditor, getAppPages, isPremiumFromBundle } from '../../..';
import { Box, CustomModalLayout, Dropdown, FormField, InputArea, Modal, SectionHelper, Text, } from '@wix/design-system';
import { useBiLogger } from '../../../hooks/use-bi-logger';
import { useTranslation } from '../../../hooks/use-translation';
import { appMarketDeleteModalCancelAppDeleteX, appMarketDeleteModalDeleteAppApproved, appMarketDeleteModalModalOpened, } from '@wix/bi-logger-app-market-data/v2';
import { useHttpClient } from '../../../hooks/use-http-client';
export function DeleteDashboardModal({ app, msId, origin, handleClose, handleDeleteApp, editorType, baseUrl, authorizationHeader, showNotification, }) {
    const t = useTranslation();
    const biLogger = useBiLogger();
    const deleteModalDropDownOptions = [
        {
            id: 'dont-need-anymore',
            value: t('manage-apps.delete-modal.option-dont-need-anymore'),
        },
        {
            id: 'missing-needed-features',
            value: t('manage-apps.delete-modal.option-missing-needed-features'),
        },
        { id: 'tried-out', value: t('manage-apps.delete-modal.option-tried-out') },
        {
            id: 'technical-issue',
            value: t('manage-apps.delete-modal.option-technical-issue'),
        },
        {
            id: 'found-alternative-solution',
            value: t('manage-apps.delete-modal.option-found-alternative-solution'),
        },
        {
            id: 'added-by-mistake',
            value: t('manage-apps.delete-modal.option-added-by-mistake'),
        },
        {
            id: 'too-expensive',
            value: t('manage-apps.delete-modal.option-too-expensive'),
        },
        {
            id: 'removed-from-market',
            value: t('manage-apps.delete-modal.option-removed-from-market'),
        },
        {
            id: 'didnt-add-it',
            value: t('manage-apps.delete-modal.option-didnt-add-it'),
        },
        { id: 'other', value: t('manage-apps.delete-modal.option-other') },
    ];
    const publishedVersion = app?.appInfo?.publishedVersion;
    const isAppPublished = publishedVersion !== '';
    const dropDownOptions = isAppPublished
        ? deleteModalDropDownOptions.filter((option) => option.id !== 'removed-from-market')
        : deleteModalDropDownOptions;
    const appName = app?.appInfo?.name;
    const appId = app?.appId;
    const [hasCounter, setHasCounter] = useState(false);
    const [deletionReason, setDeletionReason] = useState('');
    const [deletionDetails, setDeletionDetails] = useState('');
    const httpClient = useHttpClient();
    function deleteAppAndCloseModal() {
        const isTestPlan = app?.premiumInfo?.isTestPlanInstalled;
        const vendorProductId = app?.premiumInfo?.vendorProductId;
        handleDeleteApp(app, showNotification);
        const reasonIndex = dropDownOptions.findIndex((option) => option.id === deletionReason);
        if (isTestPlan) {
            httpClient
                .put(`${baseUrl}/_api/marketplace-api/v1/cancel-test-plan`, { appId, vendorProductId }, {
                headers: { Authorization: authorizationHeader },
                withCredentials: true,
            })
                .catch((error) => console.log(error));
        }
        biLogger
            .report(appMarketDeleteModalDeleteAppApproved({
            app_id: appId,
            delete_details: deletionDetails,
            delete_reason: deletionReason,
            reason_index: reasonIndex,
            market: origin === Origin.BIZMGR
                ? editorType === EditorType.ADI
                    ? 'adi_dashboard'
                    : 'dashboard'
                : origin,
            msid: msId,
            origin: 'context_menu',
            tag_type: 'manage_apps',
        }))
            .catch((e) => console.log(e));
        handleClose();
    }
    function cancelDeletionAndCloseModal() {
        biLogger
            .report(appMarketDeleteModalCancelAppDeleteX({
            app_id: appId,
            market: origin === Origin.BIZMGR
                ? editorType === EditorType.ADI
                    ? 'adi_dashboard'
                    : 'dashboard'
                : origin,
            msid: msId,
            origin: 'context_menu',
            tag_type: 'manage_apps',
        }))
            .catch((e) => console.log(e));
        handleClose();
    }
    function onSelectDeletionReason(option) {
        setDeletionReason(option.id);
    }
    function onChangeDeletionDetails(event) {
        if (event.target.value.length <= 500) {
            setDeletionDetails(event.target.value);
        }
    }
    function getNote() {
        const appUninstallAction = app?.appInfo?.actionOnUninstall;
        if (appUninstallAction === 'SHIPPING_RATES_REGION') {
            return t('manage-apps.delete-modal.note-description-shipping-region', {
                appName,
                interpolation: { escapeValue: false },
            });
        }
        if (appUninstallAction === 'REMOVE_ALL_PRODUCT') {
            return t('manage-apps.delete-modal.note-description-custom-store', {
                appName,
                interpolation: { escapeValue: false },
            });
        }
        if (isPremiumFromBundle(app)) {
            return t('manage-apps.delete-modal.note-description-bundle-app', {
                appName,
                interpolation: { escapeValue: false },
            });
        }
        if (appHasEditor(app) && getAppPages(app).length) {
            return t('manage-apps.delete-modal.note-description-page-app', {
                appName,
                interpolation: { escapeValue: false },
            });
        }
        if (appHasEditor(app)) {
            return t('manage-apps.delete-modal.note-description-editor-app', {
                appName,
                interpolation: { escapeValue: false },
            });
        }
        return t('manage-apps.delete-modal.note-description', {
            appName,
            interpolation: { escapeValue: false },
        });
    }
    useEffect(() => {
        biLogger
            .report(appMarketDeleteModalModalOpened({
            app_id: appId,
            market: origin === Origin.BIZMGR
                ? editorType === EditorType.ADI
                    ? 'adi_dashboard'
                    : 'dashboard'
                : origin,
            msid: msId,
            origin: 'context_menu',
            tag_type: 'manage_apps',
        }))
            .catch((e) => console.log(e));
    }, []);
    return (React.createElement(Modal, { isOpen: true },
        React.createElement(CustomModalLayout, { width: "600px", theme: "destructive", title: t('manage-apps.delete-modal.title', {
                appName,
                interpolation: { escapeValue: false },
            }), subtitle: t('manage-apps.delete-modal.subtitle'), primaryButtonText: t('manage-apps.delete-modal.primary-button'), primaryButtonOnClick: deleteAppAndCloseModal, primaryButtonProps: { disabled: !deletionReason }, secondaryButtonText: t('manage-apps.delete-modal.secondary-button'), secondaryButtonOnClick: cancelDeletionAndCloseModal, onCloseButtonClick: cancelDeletionAndCloseModal, dataHook: "delete-modal" },
            React.createElement(Box, { direction: "vertical", gap: "24px" },
                React.createElement(FormField, { required: true, label: t('manage-apps.delete-modal.dropdown-title') },
                    React.createElement(Dropdown, { maxHeightPixels: 180, options: dropDownOptions, onSelect: onSelectDeletionReason, dataHook: "delete-modal-dropdown", placeholder: t('manage-apps.delete-modal.dropdown-placeholder') })),
                React.createElement(FormField, { label: t('manage-apps.delete-modal.input-area-title') },
                    React.createElement(InputArea, { value: deletionDetails, onChange: onChangeDeletionDetails, onFocus: () => setHasCounter(true), onBlur: () => setHasCounter(false), hasCounter: hasCounter, maxLength: 500, placeholder: t('manage-apps.delete-modal.input-area-placeholder') })),
                appHasEditor(app) !== 0 && (React.createElement(Box, { marginBottom: "-18px" },
                    React.createElement(SectionHelper, { appearance: "warning", size: "small" },
                        React.createElement(Text, { size: "small", weight: "thin" }, t('manage-apps.delete-modal.helper'))))),
                React.createElement(Box, { gap: "4px" },
                    React.createElement(Text, { weight: "bold", size: "small" }, t('manage-apps.delete-modal.note')),
                    React.createElement(Text, { weight: "thin", size: "small" }, getNote()))))));
}
//# sourceMappingURL=delete-dashboard-modal.js.map